import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import usePageTracker from "@/hooks/usePageTracker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { CalendarIcon, ArrowLeft, Users, Plane, Home, Luggage, CreditCard, Package, Phone, Percent } from "lucide-react";

import getApiUrl from "@/utils/api";

const API = getApiUrl();

const INITIAL_NAMES = ["", "", "", "", ""];

export default function GroupBookingPage() {
  usePageTracker("Group Booking");
  const navigate = useNavigate();
  const [airports, setAirports] = useState([]);
  const [housingUnits, setHousingUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(null);
  const [useCustomAddress, setUseCustomAddress] = useState(false);
  const [passengerNames, setPassengerNames] = useState(INITIAL_NAMES);
  
  const [formData, setFormData] = useState({
    organizer_name: "",
    organizer_whatsapp: "",
    organizer_email: "",
    trip_type: "arrival",
    pickup_location: "",
    dropoff_location: "",
    housing_address: "",
    travel_date: "",
    travel_time: "",
    departure_terminal: "",
    num_passengers: 5,
    flight_info: "",
    total_suitcases: null,
    extra_suitcases: 0,
    uses_shipping_company: false,
    special_requests: "",
    payment_method: "stripe"
  });

  const [pricing, setPricing] = useState({
    trip_price: 0,
    discount_applied: 0,
    luggage_fee: 0,
    deposit: 20,
    total_due_now: 20,
    balance_due: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    calculatePricing();
  }, [formData.trip_type, formData.pickup_location, formData.num_passengers, formData.total_suitcases, formData.uses_shipping_company, airports]);

  const fetchData = async () => {
    try {
      const [airportRes, housingRes] = await Promise.all([
        axios.get(`${API}/airports`),
        axios.get(`${API}/housing-units`)
      ]);
      setAirports(airportRes.data);
      setHousingUnits(housingRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load booking options");
    }
  };

  const calculatePricing = () => {
    const airport = airports.find(a => a.name === formData.pickup_location);
    const zone = airport?.pricing_zone || "ohare";
    
    let tripPrice = 0;
    let discountAmount = 0;
    let luggageFee = 0;
    const depositPerPerson = 20;
    const totalDeposit = depositPerPerson * formData.num_passengers; // $20 per person
    let shippingSavings = 0;
    
    // Calculate extra suitcases (first is free, rest $5 each) — treat null as 0 for preview
    const totalSuitcases = Number.isInteger(formData.total_suitcases) ? formData.total_suitcases : 0;
    const extraSuitcases = Math.max(0, totalSuitcases - 1);
    
    if (formData.trip_type === "return") {
      const perPerson = formData.uses_shipping_company ? 85 : 65;
      const basePrice = perPerson * formData.num_passengers;
      
      if (formData.num_passengers >= 10) {
        discountAmount = basePrice * 0.15;
      } else if (formData.num_passengers >= 5) {
        discountAmount = basePrice * 0.10;
      }
      
      tripPrice = basePrice - discountAmount;
      luggageFee = extraSuitcases * 5;
      
      // shipping_company is now a $20/person upcharge (luggage forwarding service),
      // not a discount. Keep the variable but compute as a negative "savings" — UI
      // only renders it when positive, so this just zeroes the savings callout.
      if (formData.uses_shipping_company) {
        shippingSavings = -20 * formData.num_passengers;
      }
    } else {
      const perPerson = zone === "madison" ? 85 : 100;
      const basePrice = perPerson * formData.num_passengers;
      
      if (formData.num_passengers >= 10) {
        discountAmount = basePrice * 0.05;
      }
      
      tripPrice = basePrice - discountAmount;
      luggageFee = extraSuitcases * 5;
    }
    
    const totalDueNow = totalDeposit + luggageFee;
    const balanceDue = tripPrice - totalDeposit;
    
    setPricing({
      trip_price: tripPrice,
      discount_applied: discountAmount,
      luggage_fee: luggageFee,
      deposit: totalDeposit,
      deposit_per_person: depositPerPerson,
      shipping_savings: shippingSavings,
      total_due_now: totalDueNow,
      balance_due: balanceDue
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === "dropoff_location" && value !== "custom") {
      const housing = housingUnits.find(h => h.name === value);
      if (housing) {
        setFormData(prev => ({ ...prev, housing_address: housing.address }));
      }
    }
  };

  const handleDateSelect = (selectedDate) => {
    if (selectedDate) {
      setDate(selectedDate);
      setFormData(prev => ({ ...prev, travel_date: format(selectedDate, "yyyy-MM-dd") }));
    }
  };

  const handlePassengerNameChange = (index, value) => {
    const newNames = passengerNames.slice();
    newNames[index] = value;
    setPassengerNames(newNames);
  };

  const updatePassengerCount = (count) => {
    const newCount = Math.max(5, Math.min(20, count));
    setFormData(prev => ({ ...prev, num_passengers: newCount }));
    
    // Adjust passenger names array
    const newNames = [];
    for (let i = 0; i < newCount; i++) {
      newNames.push(passengerNames[i] || "");
    }
    setPassengerNames(newNames);
  };

  const handleSubmit = async () => {
    if (!formData.organizer_name || !formData.organizer_whatsapp || !formData.travel_date || 
        !formData.travel_time || !formData.pickup_location || !formData.flight_info) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Force explicit suitcase count selection
    if (formData.total_suitcases === null || formData.total_suitcases === undefined) {
      toast.error("Please select your total suitcase count for the group.");
      return;
    }

    // Min advance booking window (admin-configurable)
    try {
      const dt = new Date(`${formData.travel_date}T${formData.travel_time || "00:00"}:00`);
      let minHours = 48;
      try {
        const pricingRes = await axios.get(`${API}/pricing`);
        if (typeof pricingRes.data?.min_booking_hours === "number") minHours = pricingRes.data.min_booking_hours;
      } catch (_e) { /* ignore */ }
      if (!isNaN(dt.getTime()) && minHours > 0 && (dt - new Date()) / 36e5 < minHours) {
        toast.error(`Group bookings require ${minHours}+ hours notice. Please WhatsApp or call (815) 914-8139.`);
        return;
      }
    } catch (_e) { /* ignore */ }

    setLoading(true);
    try {
      let finalFormData = { 
        ...formData,
        total_suitcases: Number.isInteger(formData.total_suitcases) ? formData.total_suitcases : 0,
        passenger_names: passengerNames.filter(n => n.trim() !== "")
      };
      
      if (formData.trip_type === "return") {
        finalFormData.dropoff_location = formData.pickup_location;
        finalFormData.pickup_location = formData.dropoff_location || formData.housing_address;
      } else {
        if (useCustomAddress) {
          finalFormData.dropoff_location = formData.housing_address;
        }
      }
      
      const response = await axios.post(`${API}/group-bookings`, finalFormData);
      const bookingId = response.data.booking_id;
      
      if (formData.payment_method === "stripe") {
        const paymentRes = await axios.post(
          `${API}/payments/stripe/checkout?booking_id=${bookingId}&origin_url=${window.location.origin}`
        );
        window.location.href = paymentRes.data.checkout_url;
      } else if (formData.payment_method === "paypal") {
        localStorage.setItem("pendingBooking", JSON.stringify({ bookingId, type: "group" }));
        navigate(`/booking/success?booking_id=${bookingId}&payment=paypal&type=group`);
      } else {
        const paymentRes = await axios.post(
          `${API}/payments/manual/initiate?booking_id=${bookingId}&payment_method=${formData.payment_method}`
        );
        localStorage.setItem("paymentInfo", JSON.stringify(paymentRes.data));
        navigate(`/booking/success?booking_id=${bookingId}&payment=manual&type=group`);
      }
    } catch (error) {
      console.error("Group booking failed:", error);
      toast.error(error.response?.data?.detail || "Failed to create group booking");
    } finally {
      setLoading(false);
    }
  };

  // Generate passenger input fields
  const passengerInputs = [];
  for (let i = 0; i < formData.num_passengers; i++) {
    passengerInputs.push(
      <div key={i} className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground w-6">{i + 1}.</span>
        <Input
          value={passengerNames[i] || ""}
          onChange={(e) => handlePassengerNameChange(i, e.target.value)}
          placeholder={`Passenger ${i + 1} name`}
          className="flex-1"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4" data-testid="group-booking-page">
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-foreground mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-100 text-yellow-800 rounded-full text-sm font-semibold mb-4">
              <Percent className="w-4 h-4" />
              Group Discount Applied!
            </div>
            <h1 className="text-3xl md:text-4xl font-extrabold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Group Booking (5+ Passengers)
            </h1>
            <p className="text-muted-foreground">Special rates for groups - 10-15% off return trips!</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* Organizer Info */}
            <Card className="shadow-lg border-0 rounded-2xl">
              <CardHeader className="bg-green-600 text-white rounded-t-2xl">
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Organizer Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Organizer Name *</Label>
                    <Input
                      name="organizer_name"
                      value={formData.organizer_name}
                      onChange={handleInputChange}
                      placeholder="Full name"
                      data-testid="organizer-name-input"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>WhatsApp Number *</Label>
                    <Input
                      name="organizer_whatsapp"
                      value={formData.organizer_whatsapp}
                      onChange={handleInputChange}
                      placeholder="+1 234 567 8900"
                      data-testid="organizer-whatsapp-input"
                      className="mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label>Email (Optional)</Label>
                  <Input
                    name="organizer_email"
                    type="email"
                    value={formData.organizer_email}
                    onChange={handleInputChange}
                    placeholder="organizer@email.com"
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Trip Details */}
            <Card className="shadow-lg border-0 rounded-2xl">
              <CardHeader>
                <CardTitle>Trip Details</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label className="mb-3 block">Trip Type</Label>
                  <RadioGroup
                    value={formData.trip_type}
                    onValueChange={(value) => handleSelectChange("trip_type", value)}
                    className="grid grid-cols-2 gap-4"
                  >
                    <Label 
                      htmlFor="arrival-group"
                      className={`flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                        formData.trip_type === "arrival" ? "border-green-600 bg-green-50" : "border-muted hover:border-green-300"
                      }`}
                    >
                      <RadioGroupItem value="arrival" id="arrival-group" className="sr-only" />
                      <Plane className="w-6 h-6 mb-2 text-green-600" />
                      <span className="font-bold">Arrival</span>
                      <span className="text-xs text-center text-muted-foreground">Airport → Housing</span>
                    </Label>
                    <Label 
                      htmlFor="return-group"
                      className={`flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer transition-colors ${
                        formData.trip_type === "return" ? "border-green-600 bg-green-50" : "border-muted hover:border-green-300"
                      }`}
                    >
                      <RadioGroupItem value="return" id="return-group" className="sr-only" />
                      <Home className="w-6 h-6 mb-2 text-green-600" />
                      <span className="font-bold">Return</span>
                      <span className="text-xs text-center text-yellow-600 font-semibold">10-15% Group Discount!</span>
                    </Label>
                  </RadioGroup>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Travel Date *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          type="button"
                          className="w-full h-10 justify-start text-left font-normal mt-1"
                          data-testid="group-date-picker"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, "PPP") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[100]" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={handleDateSelect}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label>{formData.trip_type === "arrival" ? "Flight Arrival Time *" : "Flight Departure Time *"}</Label>
                    <Input
                      type="time"
                      name="travel_time"
                      value={formData.travel_time}
                      onChange={handleInputChange}
                      data-testid="group-travel-time"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>{formData.trip_type === "arrival" ? "Pickup Airport *" : "Dropoff Airport *"}</Label>
                    <Select
                      value={formData.pickup_location}
                      onValueChange={(value) => handleSelectChange("pickup_location", value)}
                    >
                      <SelectTrigger className="mt-1" data-testid="group-airport-select">
                        <SelectValue placeholder="Select airport" />
                      </SelectTrigger>
                      <SelectContent>
                        {airports.map((airport) => (
                          <SelectItem key={airport.id} value={airport.name}>
                            {airport.name} ({airport.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{formData.trip_type === "arrival" ? "Destination Housing *" : "Pickup Location *"}</Label>
                    {!useCustomAddress ? (
                      <Select
                        value={formData.dropoff_location}
                        onValueChange={(value) => handleSelectChange("dropoff_location", value)}
                      >
                        <SelectTrigger className="mt-1" data-testid="group-housing-select">
                          <SelectValue placeholder="Select housing" />
                        </SelectTrigger>
                        <SelectContent>
                          {housingUnits.map((housing) => (
                            <SelectItem key={housing.id} value={housing.name}>
                              {housing.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        name="housing_address"
                        value={formData.housing_address}
                        onChange={handleInputChange}
                        placeholder="Enter full address"
                        className="mt-1"
                      />
                    )}
                    <div className="flex items-center space-x-2 mt-2">
                      <Checkbox 
                        id="custom-group-address" 
                        checked={useCustomAddress}
                        onCheckedChange={setUseCustomAddress}
                      />
                      <label htmlFor="custom-group-address" className="text-xs text-muted-foreground cursor-pointer">
                        Enter custom address
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Flight Info (airlines, flight numbers) *</Label>
                  <Textarea
                    name="flight_info"
                    value={formData.flight_info}
                    onChange={handleInputChange}
                    placeholder="e.g., United UA123, arriving 2:30 PM"
                    data-testid="group-flight-info"
                    className="mt-1"
                    rows={2}
                  />
                </div>
                
                <div>
                  <Label>Departure Terminal</Label>
                  <Input
                    name="departure_terminal"
                    value={formData.departure_terminal}
                    onChange={handleInputChange}
                    placeholder="e.g., Terminal 1, Terminal B"
                    data-testid="group-departure-terminal"
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Group Size */}
            <Card className="shadow-lg border-0 rounded-2xl">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Group Size & Passengers</span>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => updatePassengerCount(formData.num_passengers - 1)}
                      disabled={formData.num_passengers <= 5}
                    >
                      -
                    </Button>
                    <span className="font-bold text-lg w-8 text-center">{formData.num_passengers}</span>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => updatePassengerCount(formData.num_passengers + 1)}
                      disabled={formData.num_passengers >= 20}
                    >
                      +
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {passengerInputs}
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Passenger names are optional but help us prepare for your group.
                </p>
              </CardContent>
            </Card>

            {/* Luggage — required for ALL group trips (arrival & return) */}
            <Card className={`shadow-lg border-0 rounded-2xl ${formData.total_suitcases === null || formData.total_suitcases === undefined ? 'border-red-300' : 'border-amber-200'}`}>
              <CardHeader className="bg-amber-50">
                <CardTitle className="flex items-center gap-2">
                  <Luggage className="w-5 h-5" />
                  Luggage
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label>Total Suitcases (excluding carry-on) <span className="text-red-600">*</span></Label>
                  <p className="text-xs text-green-700 mb-2 font-semibold">1st suitcase FREE · $5 for each additional suitcase (applies to all trips).</p>
                  <Select
                    value={formData.total_suitcases === null || formData.total_suitcases === undefined ? "" : String(formData.total_suitcases)}
                    onValueChange={(value) => handleSelectChange("total_suitcases", parseInt(value))}
                  >
                    <SelectTrigger className="mt-1" data-testid="group-total-suitcases-select">
                      <SelectValue placeholder="Select total suitcases (required)" />
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => {
                        const extra = Math.max(0, n - 1);
                        const cost = extra * 5;
                        const label = n === 0 ? "carry-on only - FREE" : (cost === 0 ? "FREE" : `+$${cost}`);
                        return (
                          <SelectItem key={n} value={String(n)}>
                            {n} suitcase{n !== 1 ? 's' : ''} ({label})
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Return Trip Options */}
            {formData.trip_type === "return" && (
              <Card className="shadow-lg border-0 rounded-2xl border-yellow-200">
                <CardHeader className="bg-yellow-50">
                  <CardTitle className="flex items-center gap-2">
                    <Luggage className="w-5 h-5" />
                    Return Trip Options
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  
                  <div className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border border-orange-200">
                    <div className="flex items-start space-x-3">
                      <Checkbox 
                        id="shipping-group" 
                        checked={formData.uses_shipping_company}
                        onCheckedChange={(checked) => handleSelectChange("uses_shipping_company", checked)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <label htmlFor="shipping-group" className="text-sm font-bold cursor-pointer text-orange-800">
                          <Package className="inline w-4 h-4 mr-1" />
                          Ship your luggage with T&M Courier Services
                        </label>
                        <p className="text-xs text-orange-700 mt-1">
                          <span className="font-bold text-green-700">Save $15/person</span> — fare drops from <span className="line-through">$85</span> → <span className="font-bold text-green-700">$70/person</span> when you ship with us. Bags ship separately and suitcases drop to <span className="font-bold text-green-700">$5 each</span>.
                        </p>
                        <div className="mt-2 text-xs text-gray-600">
                          <p className="font-medium mb-1">Ship your items separately:</p>
                          <div className="flex flex-wrap gap-1">
                            {["Barrels", "Suitcases", "Scooters", "Bicycles", "TVs", "Heavy Items"].map(item => (
                              <span key={item} className="px-2 py-0.5 bg-white rounded-full text-orange-700 border border-orange-200">{item}</span>
                            ))}
                          </div>
                        </div>
                        <p className="text-xs text-orange-600 mt-2">
                          📞 T&M Courier: (815) 914-8139 | Tmcourierservice1@gmail.com
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <p className="text-sm text-blue-800 font-medium">
                      Early Morning Flight? (Before 6 AM)
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      We offer late-night pickup runs (11 PM / midnight) for early morning flights. 
                      Our team will contact you to arrange the best pickup time.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Special Requests */}
            <Card className="shadow-lg border-0 rounded-2xl">
              <CardHeader>
                <CardTitle>Special Requests</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <Textarea
                  name="special_requests"
                  value={formData.special_requests}
                  onChange={handleInputChange}
                  placeholder="Any special requirements, accessibility needs, child seats, etc."
                  rows={3}
                />
              </CardContent>
            </Card>

            {/* Payment Method */}
            <Card className="shadow-lg border-0 rounded-2xl">
              <CardHeader>
                <CardTitle>Payment Method</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <RadioGroup
                  value={formData.payment_method}
                  onValueChange={(value) => handleSelectChange("payment_method", value)}
                  className="grid grid-cols-2 md:grid-cols-4 gap-4"
                >
                  <Label 
                    htmlFor="stripe-group"
                    className={`flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer ${
                      formData.payment_method === "stripe" ? "border-green-600 bg-green-50" : "border-muted"
                    }`}
                  >
                    <RadioGroupItem value="stripe" id="stripe-group" className="sr-only" />
                    <CreditCard className="w-6 h-6 mb-2" />
                    <span className="font-bold text-sm">Card</span>
                  </Label>
                  <Label 
                    htmlFor="paypal-group"
                    className={`flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer ${
                      formData.payment_method === "paypal" ? "border-green-600 bg-green-50" : "border-muted"
                    }`}
                  >
                    <RadioGroupItem value="paypal" id="paypal-group" className="sr-only" />
                    <div className="w-6 h-6 mb-2 bg-blue-700 rounded flex items-center justify-center text-white text-xs font-bold">PP</div>
                    <span className="font-bold text-sm">PayPal</span>
                  </Label>
                  <Label 
                    htmlFor="cashapp-group"
                    className={`flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer ${
                      formData.payment_method === "cashapp" ? "border-green-600 bg-green-50" : "border-muted"
                    }`}
                  >
                    <RadioGroupItem value="cashapp" id="cashapp-group" className="sr-only" />
                    <div className="w-6 h-6 mb-2 bg-green-500 rounded flex items-center justify-center text-white text-xs font-bold">$</div>
                    <span className="font-bold text-sm">CashApp</span>
                  </Label>
                  <Label 
                    htmlFor="zelle-group"
                    className={`flex flex-col items-center p-4 rounded-xl border-2 cursor-pointer ${
                      formData.payment_method === "zelle" ? "border-green-600 bg-green-50" : "border-muted"
                    }`}
                  >
                    <RadioGroupItem value="zelle" id="zelle-group" className="sr-only" />
                    <div className="w-6 h-6 mb-2 bg-purple-600 rounded flex items-center justify-center text-white text-xs font-bold">Z</div>
                    <span className="font-bold text-sm">Zelle</span>
                  </Label>
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              disabled={loading}
              data-testid="submit-group-booking-btn"
              className="w-full h-14 rounded-full bg-green-600 text-white hover:bg-green-700 text-lg font-bold"
            >
              {loading ? "Processing..." : `Book Group Trip - Pay $${pricing.total_due_now.toFixed(2)} Deposit`}
            </Button>
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <Card className="shadow-xl border-0 rounded-2xl sticky top-8">
              <CardHeader className="bg-green-600 text-white rounded-t-2xl">
                <CardTitle>Group Booking Summary</CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Passengers</span>
                  <span className="font-bold">{formData.num_passengers}</span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Trip Price</span>
                  <span className="font-semibold">${(pricing.trip_price + pricing.discount_applied).toFixed(2)}</span>
                </div>
                
                {pricing.discount_applied > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span className="flex items-center gap-1">
                      <Percent className="w-4 h-4" />
                      Group Discount
                    </span>
                    <span className="font-semibold">-${pricing.discount_applied.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">After Discount</span>
                  <span className="font-bold text-green-600">${pricing.trip_price.toFixed(2)}</span>
                </div>
                
                {pricing.luggage_fee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Luggage</span>
                    <span className="font-semibold">${pricing.luggage_fee.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Deposit (due now)</span>
                    <span>${pricing.deposit.toFixed(2)}</span>
                  </div>
                  {pricing.luggage_fee > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">+ Luggage fee</span>
                      <span>${pricing.luggage_fee.toFixed(2)}</span>
                    </div>
                  )}
                </div>
                
                <div className="border-t pt-4">
                  <div className="flex justify-between text-lg">
                    <span className="font-bold">Due Now</span>
                    <span className="font-extrabold text-green-600">${pricing.total_due_now.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm mt-2">
                    <span className="text-muted-foreground">Balance on trip day</span>
                    <span className="font-semibold">${pricing.balance_due.toFixed(2)}</span>
                  </div>
                </div>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Group Discounts:</strong>
                    <br />5+ passengers: 10% off return
                    <br />10+ passengers: 15% off return
                  </p>
                </div>
                
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <p className="text-sm font-medium text-green-800">
                    <Phone className="inline w-4 h-4 mr-1" />
                    Questions? Call (815) 914-8139
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
